/**
 * The filters in the box, one block per value — `type:GENERATION`,
 * `isRootObservation:true` — like Langfuse's query box, so what is applied
 * reads at a glance. Each block's × drops just that value.
 */

import { X } from "lucide-react";
import { FILTER_TOKEN, type ObservationFilters } from "./trace-query";

interface Block {
  column: string;
  value: string;
  drop: (f: ObservationFilters) => Partial<ObservationFilters>;
}

type ListKey = "traceNames" | "types" | "names" | "environments";

const list = (column: string, key: ListKey, f: ObservationFilters): Block[] =>
  f[key].map((value) => ({ column, value, drop: (g) => ({ [key]: g[key].filter((v) => v !== value) }) }));

/** The blocks in the box's own column order. */
export function blocks(f: ObservationFilters): Block[] {
  return [
    ...list("traceName", "traceNames", f),
    ...list("type", "types", f),
    ...list("name", "names", f),
    ...(f.root === null
      ? []
      : [{ column: "isRootObservation", value: String(f.root), drop: () => ({ root: null }) }]),
    ...list("environment", "environments", f),
    ...(f.version ? [{ column: "version", value: f.version, drop: () => ({ version: "" }) }] : []),
    ...Object.entries(f.columns).flatMap(([column, values]) =>
      values.map((value) => ({
        column,
        value,
        drop: (g: ObservationFilters) => ({ columns: without(g.columns, column, value) }),
      })),
    ),
  ];
}

/** `columns` with one value of one column dropped (and the column, once empty). */
function without(columns: Record<string, string[]>, column: string, value: string) {
  const left = (columns[column] ?? []).filter((v) => v !== value);
  const { [column]: _, ...rest } = columns;
  return left.length ? { ...rest, [column]: left } : rest;
}

export function FilterChips({
  value,
  onChange,
}: {
  value: ObservationFilters;
  onChange: (patch: Partial<ObservationFilters>) => void;
}) {
  return (
    <ul aria-label="Applied filters" className="flex flex-wrap gap-1.5">
      {blocks(value).map((b) => (
        <li
          key={`${b.column}:${b.value}`}
          className="flex items-center gap-1 rounded-md bg-chip py-0.5 pr-1 pl-2 font-mono text-xs"
        >
          <span className="text-series-2">{b.column}</span>
          <span className="text-dim">:</span>
          <span className="max-w-[220px] truncate text-series-1" title={b.value}>
            {b.value}
          </span>
          <button
            type="button"
            aria-label={`Remove ${b.column}:${b.value}`}
            className="pointer-events-auto ml-0.5 rounded p-0.5 text-faint hover:bg-hover hover:text-ink"
            onClick={() => onChange(b.drop(value))}
          >
            <X size={12} aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * The text being typed, each `column:value` drawn as a block, as Langfuse keeps
 * them while you type. It sits behind the box's transparent text with the same
 * font and wrapping, so the caret lines up; blocks take no extra width.
 */
export function QueryHighlight({ text }: { text: string }) {
  let end = 0;
  const parts = Array.from(text.matchAll(FILTER_TOKEN), (match) => {
    const gap = text.slice(end, match.index);
    end = match.index + match[0].length;
    return (
      <span key={match.index}>
        {gap}
        <span className="rounded-[3px] bg-chip shadow-[0_0_0_2px_var(--color-chip)]">
          <span className="text-series-2">{match[1]}</span>
          <span className="text-dim">:</span>
          <span className="text-series-1">{match[2]}</span>
        </span>
      </span>
    );
  });

  return (
    <>
      {parts}
      {text.slice(end)}
      {/* Keeps a trailing line break's height. */}
      {"​"}
    </>
  );
}
